/* ==========================================================================
   Volume pricing (product page)

   - Estimator: quantity field + slider. Shows the rate per piece, the
     estimated order value and the saving against the single-piece price,
     highlights the matching tier and says how far the next tier is.
   - Tier rates follow the selected variant: when the variant picker
     changes, the rates are recalculated from that variant's price.
   - Tier cards and the quote button open the bulk enquiry modal (the
     Bootstrap modal rendered by the product section). The modal's hidden
     fields and summary line are filled from the button that opened it,
     so the enquiry email says which tier and quantity were asked about.

   Data comes from a JSON block rendered by sections/volume-pricing.liquid.
   ========================================================================== */
(function () {
  'use strict';

  /* ------------------------------------------------------------- money */
  function money(cents) {
    var format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    var amount = (cents / 100).toFixed(2);
    var parts = amount.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    amount = parts.join('.');
    return format
      .replace(/\{\{\s*amount\s*\}\}/g, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, parts[0])
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','));
  }

  function clamp(n, min, max) { return Math.min(max, Math.max(min, n)); }

  /* ------------------------------------------------------- the section */
  function initSection(root) {
    if (root.dataset.vpReady) return;
    root.dataset.vpReady = '1';

    var data;
    try {
      data = JSON.parse(root.querySelector('[data-vp-data]').textContent);
    } catch (e) {
      return;
    }

    var tiers = data.tiers || [];
    if (!tiers.length) return;

    var variants = data.variants || [];
    var unit = data.unit;           // single-piece price of the selected variant, in cents
    var onRequest = data.onRequest; // made-to-order product: no rates shown

    var cards = root.querySelectorAll('[data-vp-tier]');
    var qtyInput = root.querySelector('[data-vp-qty-input]');
    var range = root.querySelector('[data-vp-range]');
    var outRate = root.querySelector('[data-vp-out-rate]');
    var outTotal = root.querySelector('[data-vp-out-total]');
    var outSave = root.querySelector('[data-vp-out-save]');
    var nudge = root.querySelector('[data-vp-nudge]');
    var nudgeText = root.querySelector('[data-vp-nudge-text]');
    var quoteBtn = root.querySelector('[data-vp-quote]');
    var quoteQty = root.querySelectorAll('[data-vp-quote-qty]');
    var sampleRate = root.querySelector('[data-vp-sample-rate]');

    var minQty = tiers[0].min;
    var maxSlider = range ? parseInt(range.max, 10) : 1000;

    /* rate for a tier at the current single-piece price */
    function rateOf(tier) {
      if (tier.fixed) return tier.fixed;
      if (!unit) return 0;
      return Math.round(unit * (100 - tier.pct) / 100);
    }

    function tierIndexFor(qty) {
      var idx = -1;
      for (var i = 0; i < tiers.length; i++) if (qty >= tiers[i].min) idx = i;
      return idx;
    }

    function savePct(rate) {
      if (!unit || !rate || rate >= unit) return 0;
      return Math.round((1 - rate / unit) * 100);
    }

    /* tier cards: rate, saving and the bar against the single-piece price */
    function paintCards() {
      cards.forEach(function (card, i) {
        var tier = tiers[i];
        var rate = rateOf(tier);
        var price = card.querySelector('[data-vp-rate]');
        var save = card.querySelector('[data-vp-save]');
        var bar = card.querySelector('[data-vp-bar]');

        if (onRequest || !rate) {
          price.textContent = data.labels.onRequest;
          save.textContent = '';
          if (bar) bar.style.setProperty('--w', '100%');
        } else {
          price.textContent = money(rate);
          var pct = savePct(rate);
          save.textContent = pct ? data.labels.save.replace('{pct}', pct) : data.labels.standard;
          if (bar && unit) bar.style.setProperty('--w', Math.max(8, Math.round(rate / unit * 100)) + '%');
        }

        card.setAttribute('data-vp-rate-text', onRequest || !rate ? '' : money(rate));
      });

      if (sampleRate && unit) sampleRate.textContent = money(unit);
    }

    /* estimator readout for a quantity */
    function update(qty, source) {
      qty = Math.max(minQty, Math.round(qty) || minQty);

      if (source !== 'input' && qtyInput) qtyInput.value = qty;
      if (source !== 'range' && range) range.value = clamp(qty, minQty, maxSlider);
      if (range) {
        var fill = (clamp(qty, minQty, maxSlider) - minQty) / (maxSlider - minQty) * 100;
        range.style.setProperty('--fill', fill + '%');
      }

      var idx = tierIndexFor(qty);
      // only mark a card as current when the estimator is there to drive it
      if (qtyInput) cards.forEach(function (card, i) {
        card.classList.toggle('is-active', i === idx);
        card.setAttribute('aria-current', i === idx ? 'true' : 'false');
      });

      var tier = tiers[idx];
      var rate = tier ? rateOf(tier) : unit;

      if (!outRate || !outTotal || !outSave) {
        // estimator switched off: nothing to fill in
      } else if (onRequest || !rate) {
        outRate.textContent = data.labels.onRequest;
        outTotal.textContent = '—';
        outSave.textContent = '—';
        outSave.classList.add('is-zero');
      } else {
        outRate.innerHTML = money(rate) + ' <small>' + data.labels.perPiece + '</small>';
        outTotal.textContent = money(rate * qty);
        var saving = unit ? (unit - rate) * qty : 0;
        outSave.textContent = saving > 0 ? money(saving) : '—';
        outSave.classList.toggle('is-zero', !(saving > 0));
      }

      // How far is the next tier, and what does it change?
      var next = tiers[idx + 1];
      if (nudge) {
        if (next) {
          var more = next.min - qty;
          var text = data.labels.nudge
            .replace('{more}', more.toLocaleString())
            .replace('{tier}', next.label);
          if (!onRequest && rate && rateOf(next) < rate) {
            text += ' ' + data.labels.nudgeSave.replace('{rate}', money(rateOf(next)));
          }
          nudgeText.innerHTML = text;
          nudge.hidden = false;
        } else {
          nudgeText.innerHTML = data.labels.top;
          nudge.hidden = false;
        }
      }

      quoteQty.forEach(function (el) { el.textContent = qty.toLocaleString(); });
      if (quoteBtn) {
        quoteBtn.setAttribute('data-vp-qty', qty);
        quoteBtn.setAttribute('data-vp-label', tier ? tier.label : '');
        quoteBtn.setAttribute('data-vp-rate-text', onRequest || !rate ? '' : money(rate));
      }
    }

    function currentQty() {
      return parseInt(qtyInput && qtyInput.value, 10) || minQty;
    }

    /* ---- controls ---- */
    if (range) {
      range.addEventListener('input', function () { update(parseInt(range.value, 10), 'range'); });
    }
    if (qtyInput) {
      qtyInput.addEventListener('input', function () {
        var v = parseInt(qtyInput.value, 10);
        if (v >= minQty) update(v, 'input');
      });
      qtyInput.addEventListener('change', function () { update(currentQty()); });
    }
    root.querySelectorAll('[data-vp-step]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var step = parseInt(btn.getAttribute('data-vp-step'), 10);
        update(currentQty() + step);
      });
    });

    /* a tier card also moves the estimator to that tier */
    cards.forEach(function (card, i) {
      card.addEventListener('click', function () {
        var q = currentQty();
        var inTier = q >= tiers[i].min && (!tiers[i + 1] || q < tiers[i + 1].min);
        if (!inTier) update(tiers[i].min);
        card.setAttribute('data-vp-qty', inTier ? q : tiers[i].min);
      });
    });

    /* ---- follow the variant picker ---- */
    function syncVariant() {
      var idField = document.querySelector('form[data-product-form] input[name="id"]');
      if (!idField) return;
      var id = parseInt(idField.value, 10);
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].id === id) {
          if (variants[i].price !== unit) {
            unit = variants[i].price;
            paintCards();
            update(currentQty());
          }
          return;
        }
      }
    }
    document.addEventListener('change', function (e) {
      if (e.target.closest && e.target.closest('[data-variant-picker]')) setTimeout(syncVariant, 30);
    });

    paintCards();
    update(data.startQty || minQty);
    syncVariant();
  }

  /* ------------------------------------------- bulk enquiry modal prefill */
  function initModal() {
    var modal = document.getElementById('enquiryModal');
    if (!modal || modal.dataset.vpReady) return;
    modal.dataset.vpReady = '1';

    var summary = modal.querySelector('[data-enquiry-tier]');
    var fTier = modal.querySelector('[data-enquiry-field="tier"]');
    var fQty = modal.querySelector('[data-enquiry-field="quantity"]');
    var fRate = modal.querySelector('[data-enquiry-field="rate"]');
    var fType = modal.querySelector('input[name="contact[enquiry_type]"]');
    var message = modal.querySelector('textarea[name="contact[body]"]');
    var product = modal.querySelector('input[name="contact[product]"]');

    function setField(field, value) {
      if (!field) return;
      field.value = value || '';
      field.disabled = !value;   // empty fields are left out of the email
    }

    modal.addEventListener('show.bs.modal', function (event) {
      var trigger = event.relatedTarget;
      var qty = trigger && trigger.getAttribute('data-vp-qty');
      var label = trigger && trigger.getAttribute('data-vp-label');
      var rate = trigger && trigger.getAttribute('data-vp-rate-text');

      setField(fTier, label);
      setField(fQty, qty ? qty + ' pcs' : '');
      setField(fRate, rate ? rate + ' / pc (indicative)' : '');
      if (fType) fType.value = qty ? 'Volume quote' : 'Bulk enquiry';

      if (summary) {
        if (qty) {
          summary.innerHTML =
            '<span class="enquiry-tier__key">Volume quote</span>' +
            '<strong>' + Number(qty).toLocaleString() + ' pcs</strong>' +
            (label ? '<span>' + label + '</span>' : '') +
            (rate ? '<span>· indicative ' + rate + ' / pc</span>' : '');
          summary.hidden = false;
        } else {
          summary.hidden = true;
        }
      }

      // Start the message for them, but never overwrite what they typed.
      if (message && (!message.value || message.dataset.vpAuto === '1')) {
        if (qty) {
          var name = product ? product.value : 'this product';
          message.value = 'We are interested in approximately ' + Number(qty).toLocaleString() +
            ' pcs of ' + name + '. Please share pricing, lead time and packaging options.';
          message.dataset.vpAuto = '1';
        } else if (message.dataset.vpAuto === '1') {
          message.value = '';
          delete message.dataset.vpAuto;
        }
      }
    });

    if (message) {
      message.addEventListener('input', function () { delete message.dataset.vpAuto; });
    }
  }

  /* ---------------------------------------------------------------- init */
  function init(scope) {
    (scope || document).querySelectorAll('[data-vp]').forEach(initSection);
    initModal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
